import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useScrollLock } from "../lib/useScrollLock";
import { useBackButtonClose } from "../lib/useBackButtonClose";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Extra classes on the sheet panel (e.g. height, max-width overrides) */
  className?: string;
}

/**
 * Slides in from the bottom on mobile, centered on larger screens.
 * Handles Escape key and backdrop click. Use AnimatePresence exit animation.
 */
export function SheetModal({ isOpen, onClose, children, className = "" }: Props) {
  useScrollLock(isOpen);
  useBackButtonClose(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        // Must be a motion element so AnimatePresence defers this subtree's
        // unmount until the nested panel's exit finishes (a plain <div> gets
        // yanked synchronously, cutting off the close animation on desktop).
        <motion.div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center sm:p-4"
          onClick={onClose}
        >
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
          <motion.div
            className={`relative bg-white dark:bg-neutral-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full flex flex-col overflow-y-auto overscroll-contain max-h-[90dvh] sm:max-h-[85dvh] ${className}`}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
